
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { CreateBoardDialog } from "./board/CreateBoardDialog";
import { BoardCard } from "./board/BoardCard";

type Board = {
  id: string;
  name: string;
  emoji: string;
  is_default: boolean;
};

type BoardManagementProps = {
  companyId: string;
  companyPlan: string;
  boards: Board[];
  onBoardsUpdate: () => void;
};

export const BoardManagement = ({ companyId, companyPlan, boards: initialBoards, onBoardsUpdate }: BoardManagementProps) => {
  const { toast } = useToast();
  const [boards, setBoards] = useState<Board[]>(initialBoards);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBoards();
  }, [companyId]);

  const fetchBoards = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("feedback_boards")
        .select("*")
        .eq("company_id", companyId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      
      setBoards(data || []);
    } catch (error) {
      console.error("Error fetching boards:", error);
      toast({
        title: "Error",
        description: "Failed to load feedback boards",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteBoard = async (boardId: string) => {
    try {
      const { error } = await supabase
        .from("feedback_boards")
        .delete()
        .eq("id", boardId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Board deleted successfully",
      });
      
      fetchBoards();
      if (onBoardsUpdate) onBoardsUpdate();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete board",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Feedback Boards</h2>
        <CreateBoardDialog 
          companyId={companyId}
          companyPlan={companyPlan}
          currentBoardCount={boards.length}
          onBoardCreated={() => {
            fetchBoards();
            if (onBoardsUpdate) onBoardsUpdate();
          }}
        />
      </div>

      {loading ? (
        <div className="text-center text-muted-foreground">Loading boards...</div>
      ) : boards.length === 0 ? (
        <div className="text-center text-muted-foreground">
          No boards yet. Create your first board to start collecting feedback.
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {boards.map((board) => (
            <BoardCard
              key={board.id}
              id={board.id}
              name={board.name}
              emoji={board.emoji}
              isDefault={board.is_default}
              onDelete={deleteBoard}
            />
          ))}
        </div>
      )}
    </div>
  );
};
